import React, { useState, useEffect, useMemo } from "react";
import "../styles/BuyCredits.css";
import { useNavigate } from "react-router-dom";
import CreditsController from "../controllers/CreditsController";
import { userService } from "../services/userService";

import StarterIcon from "../icons/BusinessHub/StarterIcon";
import PopulariIcon from "../icons/BusinessHub/PopularIcon";
import ProIcon from "../icons/BusinessHub/ProIcon";
import Entreprise from "../icons/BusinessHub/EntrepriceIcon";
import ProductBoostIcon from "../icons/BusinessHub/ProductBoost";
import SponsoredAdIcon from "../icons/BusinessHub/SponsoredAd";
import PriorityListingIcon from "../icons/BusinessHub/PriorityListing";
import HomePageFeatureIcon from "../icons/BusinessHub/HomePageFeature";
import WalletIcon from "../icons/Wallet";
import CheckIcon from "../icons/BusinessHub/CheckIcon";
import Utils from "../utils/Utils";

function BuyCredits() {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);

        const jwt = userService.getUser();

        const res = await CreditsController.getBuyCreditsPopup({ jwt });

        const apiData = res?.data || res?.result || res;

        setPlans(apiData?.plans || []);
        setSelectedPlan(0);
      } catch (err) {
        console.error("Buy credits error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleBuyCredits = async () => {
    try {
      const user = userService.getUser();

      const decoded = Utils.parseJwt(user);

      const createdById = Object.values(decoded?.userDetails || {})?.[0]
        ?.employeeId;

      const companyId = userService.getCompID();

      const selected = normalizedPlans?.[selectedPlan];

      if (!selected) {
        // console.error("No selected plan");
        return;
      }

      const payload = {
        billingPlanId: selected.id,
        companyId: companyId,
        createdById: createdById,
      };

      const response = await CreditsController.BuyCredits({
        jwt: user,
        request: payload,
      });
    } catch (err) {
      console.error("BUY CREDITS ERROR:", err);
    }
  };

  const iconMap = {
    "Growth Boost": <StarterIcon />,
    "Sales Accelerator": <PopulariIcon />,
    "Max Revenue Engine": <ProIcon />,
  };

  const normalizedPlans = useMemo(() => {
    return plans.map((p) => ({
      id: p.billingPlanId,
      name: p.planName,
      credits: p.creditsIncluded,
      price: `$${p.price.toFixed(2)}`,
      rate: `$${p.pricePerCredit.toFixed(3)}/cr`,
      badge: p.mostPopular ? "Most Popular" : null,
      icon: iconMap[p.planName] || <WalletIcon />,
      description: p.description,
    }));
  }, [plans]);

  const selected = normalizedPlans?.[selectedPlan];

  const handleClose = () => {
    navigate("/user-management", {
      state: { active: "I" },
    });
  };

  return (
    <div className="buy-credits-page">
      <div className="buy-credits-top">
        <div>
          <div className="buy-credits-title">Buy Azalea Credits</div>
          <div className="buy-credits-subtitle">
            Credits are used for promotions, sponsored listings, and priority
            placement.
          </div>
        </div>

        <div className="buy-credits-close" onClick={handleClose}>
          ×
        </div>
      </div>

      <div className="buy-credit-plans">
        {normalizedPlans.map((plan, index) => (
          <div
            key={index}
            className={`buy-credit-card ${
              selectedPlan === index ? "buy-credit-card-active" : ""
            }`}
            onClick={() => {
              if (normalizedPlans[index]) {
                setSelectedPlan(index);
              }
            }}
          >
            {plan.badge && (
              <div
                className={
                  plan.badge === "Best Value"
                    ? "buy-credit-badge-purple"
                    : "buy-credit-badge-blue"
                }
              >
                {plan.badge}
              </div>
            )}

            {selectedPlan === index && (
              <div className="buy-credit-check">
                <CheckIcon />
              </div>
            )}

            <div className="buy-credit-icon">{plan.icon}</div>

            <div className="buy-credit-name">{plan.name}</div>
            <div className="buy-credit-amount">{plan.credits}</div>
            <div className="buy-credit-note">credits</div>

            <div className="buy-credit-bottom">
              <div className="buy-credit-price">{plan.price}</div>
              <div className="buy-credit-rate">{plan.rate}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="buy-credit-unlocks">
        <div className="buy-credit-unlocks-title">What credits unlock</div>

        <div className="buy-credit-unlocks-grid">
          <div className="buy-credit-unlock-item">
            <div className="buy-credit-unlock-icon">
              <ProductBoostIcon />
            </div>
            <div>
              <div>Product Boost</div>
              <span>50credits</span>
            </div>
          </div>

          <div className="buy-credit-unlock-item">
            <div className="buy-credit-unlock-icon">
              <SponsoredAdIcon />
            </div>
            <div>
              <div>Sponsored Ad</div>
              <span>100credits</span>
            </div>
          </div>

          <div className="buy-credit-unlock-item">
            <div className="buy-credit-unlock-icon">
              <PriorityListingIcon />
            </div>
            <div>
              <div>Priority Listing</div>
              <span>25credits</span>
            </div>
          </div>

          <div className="buy-credit-unlock-item">
            <div className="buy-credit-unlock-icon">
              <HomePageFeatureIcon />
            </div>
            <div>
              <div>Homepage Feature</div>
              <span>150credits</span>
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <button className="buy-credit-submit" onClick={handleBuyCredits}>
          Continue - {selected.credits} credits for {selected.price}
        </button>
      )}
    </div>
  );
}

export default BuyCredits;
